/**
 * Guest-eater display helpers.
 *
 * Anonymous participants in a dining session are backed by a real "guest eater" whose email is a
 * deterministic, internal address (`guest-<anonId>@guest.resactly.local`) and whose first/last name
 * are usually null. Those internals must never leak into member-facing UI: a guest should read as a
 * clean "Guest", never "Guest null" / "null null", and their synthetic email must stay hidden.
 */

const GUEST_EMAIL_DOMAIN = '@guest.resactly.local'

/** True for the deterministic guest-eater email — the address we must not surface to users. */
export function isGuestEmail(email?: string | null): boolean {
  return !!email && email.toLowerCase().endsWith(GUEST_EMAIL_DOMAIN)
}

/**
 * Cleans a member display name built as `${firstName} ${lastName}`: drops stray "null"/"undefined"
 * tokens (from null name parts) and collapses whitespace. Returns '' when nothing usable remains.
 */
export function cleanMemberName(name?: string | null): string {
  if (!name) return ''
  return name
    .split(/\s+/)
    .filter((part) => {
      const lower = part.toLowerCase()
      return part.length > 0 && lower !== 'null' && lower !== 'undefined'
    })
    .join(' ')
    .trim()
}

/**
 * A member-facing display name. Real users keep their name; a guest (or an unnamed member) falls
 * back to a friendly label ("Guest") instead of "Guest null" / "null null".
 */
export function memberDisplayName(
  name?: string | null,
  email?: string | null,
  fallback = 'Guest',
): string {
  const cleaned = cleanMemberName(name)
  if (cleaned) return cleaned
  return isGuestEmail(email) ? 'Guest' : fallback
}
